/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { pluralize, Icon } from 'sarsaparilla';

import type { SearchResultItem } from 'shared-ui/api/search/search.types';

import styles from './CardMeta.module.scss';

interface CardMetaProps {
    item: SearchResultItem;
}

function getIsDayUse(item: SearchResultItem) {
    if (item.entity_type !== 'campground') return false;

    if (
        item.campsite_type_of_use &&
        item.campsite_type_of_use.length > 0 &&
        item.campsite_type_of_use[0] !== 'na'
    ) {
        return !item.campsite_type_of_use.includes('Overnight');
    }

    return false;
}

export function CardMeta({ item }: CardMetaProps) {
    const isDayUse = getIsDayUse(item);
    const numAccessibleCampsites = item.accessible_campsites_count
        ? Number(item.accessible_campsites_count)
        : null;
    const numCampgrounds = item.campgrounds_count ? Number(item.campgrounds_count) : null;
    const numPermits = item.permits_count ? Number(item.permits_count) : null;
    const numTicketFacilities = item.ticket_facilities_count
        ? Number(item.ticket_facilities_count)
        : null;
    const numActivityPasses = item.activitypass_count
        ? Number(item.activitypass_count)
        : null;
    const numTreePermits = item.treepermits_count ? Number(item.treepermits_count) : null;
    const numVenues = item.venuereservations_count
        ? Number(item.venuereservations_count)
        : null;

    const accessibleSiteLabelSingular = isDayUse
        ? 'Accessible site'
        : 'Accessible campsite';
    const accessibleSiteLabelPlural = isDayUse
        ? 'Accessible sites'
        : 'Accessible campsites';

    return (
        <ul className={styles.metaList}>
            {numCampgrounds ? (
                <li className={styles.item}>
                    <Icon iconName="tent" />
                    <span>
                        {`${numCampgrounds} ${pluralize(numCampgrounds, 'Campground', 'Campgrounds')}`}
                    </span>
                </li>
            ) : null}

            {numAccessibleCampsites ? (
                <li className={styles.item}>
                    <Icon iconName="accessible" />
                    <span>
                        {`${numAccessibleCampsites} ${pluralize(numAccessibleCampsites, accessibleSiteLabelSingular, accessibleSiteLabelPlural)}`}
                    </span>
                </li>
            ) : null}

            {numPermits ? (
                <li className={styles.item}>
                    <Icon iconName="permit" size="sm" />
                    <span>
                        {`${numPermits} ${pluralize(numPermits, 'Permit', 'Permits')}`}
                    </span>
                </li>
            ) : null}

            {numTicketFacilities ? (
                <li className={styles.item}>
                    <Icon iconName="ticket" size="sm" />
                    <span>
                        {`${numTicketFacilities} ${pluralize(numTicketFacilities, 'Tour facility', 'Tour facilities')}`}
                    </span>
                </li>
            ) : null}

            {numActivityPasses ? (
                <li className={styles.item}>
                    <Icon iconName="card" size="sm" />
                    <span>
                        {`${numActivityPasses} ${pluralize(numActivityPasses, 'Activity pass', 'Activity passes')}`}
                    </span>
                </li>
            ) : null}

            {numTreePermits ? (
                <li className={styles.item}>
                    <Icon iconName="tree" size="sm" />
                    <span>
                        {`${numTreePermits} ${pluralize(numTreePermits, 'Tree permit', 'Tree permits')}`}
                    </span>
                </li>
            ) : null}

            {numVenues ? (
                <li className={styles.item}>
                    <Icon iconName="celebration" />
                    <span>{`${numVenues} ${pluralize(numVenues, 'Venue', 'Venues')}`}</span>
                </li>
            ) : null}
        </ul>
    );
}

// cSpell:ignore activitypass, treepermits, venuereservations
